import { useState } from 'react';
import { parseAllForSequence } from '../../Core/CsSeqDia/CsSeqImportStrategy';
import { buildCanvasSequenceLayout } from '../../Core/CsSeqDia/CsSeqCanvasLayoutBuilder';
import { mergeSequenceLayoutIntoCanvas } from '../../Core/CsSeqDia/CsSeqDiaRegenerateMerger';
import { buildSequenceShape } from '../../Core/CsSeqDia/CsSeqShapeFactory';
import { buildMermaidSequence } from '../../Core/CsSeqDia/CsSeqMermaidBuilder';
import { walkCallChain } from '../../Core/CsSeqDia/CsSeqOrchestrator';
import { extractProducesResponses } from '../../Core/CsSeqDia/CsProducesResponseScanner';

/**
 * Parses dropped files (same entries as UmlSchemaFileImportTab's file list)
 * for sequence-diagram use: reads text, parses all files, exposes the
 * resulting entry-point options (class+method pairs with a body), and a
 * generate() to produce mermaid source for a chosen entry point.
 * @param {{droppedFiles: object[]}} params
 * @returns {{parseFiles: () => Promise<void>, isParsing: boolean, entryPointOptions: {className:string, methodName:string}[], parseWarnings: string[], generate: (className: string, methodName: string) => void, mermaidSource: string, generateWarnings: string[]}}
 */
export default function useSeqDiaGenerate({ droppedFiles }) {
  const [isParsing, setIsParsing] = useState(false);
  const [allParsedTypes, setAllParsedTypes] = useState([]);
  const [rawFileSources, setRawFileSources] = useState([]); // kept alongside allParsedTypes — CsClassParser strips attributes before parsing, so ProducesResponseType extraction (see CsProducesResponseScanner) needs the ORIGINAL text
  const [entryPointOptions, setEntryPointOptions] = useState([]);
  const [parseWarnings, setParseWarnings] = useState([]);
  const [mermaidSource, setMermaidSource] = useState('');
  const [generateWarnings, setGenerateWarnings] = useState([]);

  const parseFiles = async () => {
    setIsParsing(true);
    const fileSources = await Promise.all(
      droppedFiles.map(async function toSource(entry) {
        return { name: entry.name, source: await entry.file.text() };
      })
    );
    const result = parseAllForSequence(fileSources);
    setAllParsedTypes(result.allParsedTypes);
    setRawFileSources(fileSources);
    setEntryPointOptions(result.entryPointOptions);
    setParseWarnings(result.warnings);
    setIsParsing(false);
  };

  const generate = (className, methodName) => {
    // walkCallChain once, feeds BOTH the mermaid text (kept as secondary/debug
    // view) and the canvas shapes (primary output).
    const chain = walkCallChain(allParsedTypes, className, methodName);

    // [ProducesResponseType(...)] on the entry method (typical ASP.NET
    // controller action) — first file whose raw text actually contains the
    // method wins; good enough since method names are rarely duplicated
    // across dropped files in the same trace.
    const producesResponses = rawFileSources.reduce(function findFirst(found, file) {
      if (found.length > 0) return found;
      return extractProducesResponses(file.source, methodName);
    }, []);

    const shape = buildSequenceShape(chain, className, methodName, producesResponses);
    setMermaidSource(buildMermaidSequence(shape));
    setGenerateWarnings(chain.warnings);

    const layout = buildCanvasSequenceLayout(shape);
    mergeSequenceLayoutIntoCanvas(layout);
  };

  return { parseFiles, isParsing, entryPointOptions, parseWarnings, generate, mermaidSource, generateWarnings };
}
